import './VertNavBar.scss';
import {
  AddBookmark,
  Bookmark,
  Category,
  NavigationData,
  NavigationItemType,
  Report,
  ReportType,
} from 'core/slices/models';
import React, { useCallback, useEffect, useState } from 'react';
import ReportNavItem from 'components/VertNavBar/components/ReportNavItem';
import CategoryNavItem from 'components/VertNavBar/components/CategoryNavItem';
import { VertNavSearchBox } from './components/VertNavSearchBox';
import { ReportSelectOptionType } from '../../types/reports';
import { encodeB64 } from '../../layouts/functions/reportUtils';
import { Accordion } from 'react-bootstrap';
import BookmarkManager from 'components/VertNavBar/components/BookmarkManager';
import { AppDispatch, useAppSelector } from 'core/store';
import {
  useCreateBookmarkMutation,
  useDeleteBookmarkMutation,
  useUpdateBookmarkMutation,
} from 'core/services/BookmarkApiSlice';
import { TOAST_FAIL, TOAST_SUCCESS, Toaster } from 'components/Toaster/Toaster';
import { toastOpen } from 'core/slices/toasts';
import { selectActiveProjectKey } from 'core/slices/projects';
import { useDispatch, useSelector } from 'react-redux';
import addBookmarkToNavData from 'components/VertNavBar/functions/addBookmarkToNavData';
import removeBookmarkFromNavData from 'components/VertNavBar/functions/removeBookmarkFromNavData';
import { flattenReports } from 'domain/functions/flattenReports';
import { getActiveCategoryByActiveReport } from 'domain/functions/getActiveCategoryByActiveReport';
import { getReportType } from 'components/VertNavBar/functions/getReportType';

const getNavChildUniqueId = (child: Report | Category): string => {
  if (child.nodeType === NavigationItemType.REPORT) {
    return `report:${child.id}`;
  }

  return `category:${(child as Category).id || (child as Category).categoryName}`;
};

interface VertNavBarProps {
  navigationData: NavigationData | null;
  activeReport?: Report;
  onReportClick?: (report: Report) => void;
  onBookmarkClick?: (bookmark: Bookmark) => void;
}

interface BookmarkData {
  projectKey: string;
  reportId: string;
  bookmark: AddBookmark | Bookmark;
}

export const VertNavBar: React.FC<VertNavBarProps> = ({
  navigationData,
  onReportClick,
  onBookmarkClick,
  activeReport,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [reportOptions, setReportOptions] = useState<ReportSelectOptionType[]>(
    []
  );
  const [selectedOption, setSelectedOption] =
    useState<ReportSelectOptionType | null>(null);
  const [activeCategory, setActiveCategory] = useState<Category | null>(null);
  const [editBookmark, setEditBookmark] = useState<
    AddBookmark | Bookmark | undefined
  >();
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [showBookmarkManager, setShowBookmarkManager] = useState(false);
  const [newBookmarkKey, setNewBookmarkKey] = useState(0); // New state for unique key for new bookmarks
  const [navigationState, setNavigationState] = useState<NavigationData | null>(
    navigationData
  );

  const activeProjectKey = useSelector(selectActiveProjectKey);
  const [createBookmark] = useCreateBookmarkMutation();
  const [updateBookmark] = useUpdateBookmarkMutation();
  const [deleteBookmark] = useDeleteBookmarkMutation();

  useEffect(() => {
    setNavigationState(navigationData);
  }, [navigationData]);

  useEffect(() => {
    const allReports = flattenReports(navigationState);
    setReportOptions(allReports);
  }, [navigationState]);

  useEffect(() => {
    if (activeReport) {
      const activeCat = getActiveCategoryByActiveReport(
        navigationState,
        activeReport
      );
      if (activeCat) {
        setActiveCategory(activeCat as Category);
        setActiveKey(activeCat?.categoryName);
      }
    }
  }, [activeReport, navigationState]);

  const handleReportFilterChange = useCallback(
    (selected: ReportSelectOptionType | null) => {
      setSelectedOption(selected);
      if (selected && activeReport?.id !== selected.reportData.id) {
        onReportClick?.(selected.reportData);
      }
    },
    [activeReport?.id, onReportClick]
  );

  const handleReportClick = useCallback(
    (report: Report) => {
      onReportClick?.(report);

      const matchedOption = reportOptions.find(o => o.value === report.id);
      if (matchedOption) setSelectedOption(matchedOption);
    },
    [activeReport?.id, onReportClick, reportOptions]
  );

  const handleAccordionSelect = (eventkey: any) => {
    setActiveKey(eventkey);
  };

  const handleBookmarkClick = (bookmark: Bookmark) => {
    onBookmarkClick && onBookmarkClick(bookmark);
  };

  const handleUpdateBookmark = async (bookmark: Bookmark) => {
    setShowBookmarkManager(false);
    try {
      let bookmarkData: BookmarkData | null = null;

      if (bookmark.type === ReportType.PBI) {
        bookmarkData = await captureBookmarkState(bookmark);
      } else {
        const searchParams = new URLSearchParams(window.location.search);
        bookmark.reportState = encodeB64(searchParams.toString());
        bookmarkData = {
          projectKey: activeProjectKey,
          reportId: activeReport!.id,
          bookmark: bookmark,
        };
      }
      if (bookmarkData) {
        await updateBookmark({
          projectKey: activeProjectKey,
          reportId: bookmark!.reportId,
          id: bookmark!.id,
          bookmark: bookmarkData.bookmark,
        })
          .unwrap()
          .then(() => {
            if (navigationState) {
              const newNavData = removeBookmarkFromNavData(
                navigationState,
                bookmark.reportId,
                bookmark.id
              );
              const newNavData2 = addBookmarkToNavData(
                newNavData,
                bookmark.reportId,
                bookmark
              );
              setNavigationState(newNavData2);
            }
          });
      }
    } catch (error) {
      dispatch(
        toastOpen({
          className: TOAST_FAIL,
          text: 'Error occurred when creating the bookmark.',
        })
      );
    }
  };

  const handleDeleteBookmark = async (bookmark: Bookmark) => {
    setShowBookmarkManager(false);
    try {
      await deleteBookmark({
        projectKey: activeProjectKey,
        reportId: bookmark!.reportId,
        id: bookmark.id,
      })
        .unwrap()
        .then(() => {
          if (navigationState) {
            const newNavData = removeBookmarkFromNavData(
              navigationState,
              bookmark.reportId,
              bookmark.id
            );
            setNavigationState(newNavData);
          }
        });
    } catch (error) {
      dispatch(
        toastOpen({
          className: TOAST_FAIL,
          text: 'Error occurred when creating the bookmark.',
        })
      );
    }
  };

  /**
   * Handles the creation/storage of a new bookmark by capturing the current report state
   * @param bookmark
   */
  const handleCreateBookmark = async (bookmark: AddBookmark) => {
    setShowBookmarkManager(false);
    let bookmarkData: BookmarkData | null = null;

    if (getReportType(activeReport) === ReportType.PBI) {
      bookmarkData = await captureBookmarkState(bookmark);
    } else {
      const searchParams = new URLSearchParams(window.location.search);
      bookmark.reportState = encodeB64(searchParams.toString());
      bookmark.type = ReportType.TABLE;

      bookmarkData = {
        projectKey: activeProjectKey,
        reportId: activeReport!.id,
        bookmark: bookmark,
      };
    }
    if (bookmarkData) {
      try {
        const newBookmark = await createBookmark(bookmarkData).unwrap();

        dispatch(
          toastOpen({
            className: TOAST_SUCCESS,
            text: 'Your bookmark is saved successfully.',
          })
        );

        if (navigationState) {
          const newNavData = addBookmarkToNavData(
            navigationState,
            bookmarkData.reportId,
            newBookmark
          );
          setNavigationState(newNavData);
        }
      } catch (error) {
        dispatch(
          toastOpen({
            className: TOAST_FAIL,
            text: 'Error occurred when creating the bookmark.',
          })
        );
      }
    }
  };

  const handleEditBookmarkMenuClick = (bookmark?: Bookmark) => {
    // TODO implement delete bookmark logic
    setNewBookmarkKey(prevKey => prevKey + 1); // Increment key for new bookmark
    if (!bookmark) {
      const reportType = getReportType(activeReport);
      setEditBookmark({
        type: reportType,
        name: '',
      } as AddBookmark);
    } else {
      setEditBookmark(bookmark);
    }
    setShowBookmarkManager(true);
  };

  // This is the core function that captures the bookmark state and prepares the data
  const report = useAppSelector(state => state.pbiReportSlice.report);
  const captureBookmarkState = async (
    bookmark: AddBookmark | Bookmark
  ): Promise<BookmarkData | null> => {
    try {
      const reportBookmark = await report?.bookmarksManager.capture({
        personalizeVisuals: true,
      });

      if (reportBookmark?.state && activeReport) {
        return {
          projectKey: activeProjectKey,
          reportId: activeReport.id,
          bookmark: {
            name: bookmark.name, // Assuming 'bookmark' is available in scope
            reportState: reportBookmark.state,
            type: getReportType(activeReport), // Assuming getReportType() is available in scope
          },
        };
      }
      return null;
    } catch (error) {
      dispatch(
        toastOpen({
          className: TOAST_FAIL,
          text: 'Error occurred when capturing report state.',
        })
      );
      return null;
    }
  };

  if (!navigationState) return <></>;

  const uniqueRootChildren = (navigationState.children || []).filter(
    (child, index, children) =>
      index ===
      children.findIndex(
        candidate =>
          getNavChildUniqueId(candidate) === getNavChildUniqueId(child)
      )
  );

  return (
    <div className="vertical-nav-bar">
      <Toaster />
      <VertNavSearchBox
        reportOptions={reportOptions}
        selectedOption={selectedOption}
        onChange={handleReportFilterChange}
      />
      <div id="vert-nav-bar">
        <Accordion flush activeKey={activeKey} onSelect={handleAccordionSelect}>
          {uniqueRootChildren.map(child => {
            switch (child.nodeType) {
              case NavigationItemType.REPORT:
                return (
                  <ReportNavItem
                    key={getNavChildUniqueId(child)}
                    elem={child as Report}
                    onReportClick={handleReportClick}
                    onBookmarkClick={handleBookmarkClick}
                    onEditBookmarkClick={handleEditBookmarkMenuClick}
                    activeReport={activeReport}
                  />
                );
              case NavigationItemType.CATEGORY:
                return (
                  <CategoryNavItem
                    key={getNavChildUniqueId(child)}
                    elem={child as Category}
                    onReportClick={handleReportClick}
                    onBookmarkClick={handleBookmarkClick}
                    onEditBookmarkClick={handleEditBookmarkMenuClick}
                    activeReport={activeReport}
                  />
                );
              default:
                return <></>;
            }
          })}
        </Accordion>
        <BookmarkManager
          key={newBookmarkKey}
          bookmark={editBookmark}
          show={showBookmarkManager}
          onCreateBookmark={handleCreateBookmark}
          onUpdateBookmark={handleUpdateBookmark}
          onDeleteBookmark={handleDeleteBookmark}
          handleClose={() => setShowBookmarkManager(false)}
        />
      </div>
    </div>
  );
};

export default VertNavBar;
